import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import {
  CatalogueApiService,
  UserInteractionApiService,
} from '@angular-workspace/services';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import {
  DetailHeaderComponent,
  DetailHeaderConfig,
} from '../../../shared/components/detail-header/detail-header.component';
import { HelperService } from '../../../shared/services/helper.service';
import { FormatService } from '../../../shared/services/format.service';
import { ToasterService } from '../../../services/toaster.service';
import { SkeletonComponent } from '../../../shared/components/skeleton/skeleton.component';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { ModelDetailModelTabComponent } from '../model-tab/model-tab.component';
import { parseBookmarkLikeFromUserInteractions } from '../../../shared/utils/user-interaction-bookmark-like';

@Component({
  selector: 'app-model-details-home',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    DetailHeaderComponent,
    LoginModalComponent,
    ModelDetailModelTabComponent,
    SkeletonComponent,
  ],
  templateUrl: './model-details-home.html',
  styleUrl: './model-details-home.scss',
})
export class ModelDetailsHome implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly catalogueApi = inject(CatalogueApiService);
  private readonly userInteractionApi = inject(UserInteractionApiService);
  private readonly keycloak = inject(KeycloakService);
  private readonly helper = inject(HelperService);
  private readonly formatService = inject(FormatService);
  private readonly toaster = inject(ToasterService);

  readonly loading = signal(true);
  readonly isLoggedIn = signal(false);
  readonly showLoginModal = signal(false);
  readonly isBookmarked = signal(false);
  readonly isLiked = signal(false);

  modelId: string | null = null;

  headerConfig: DetailHeaderConfig = {
    title: '',
    shortDescription: '',
    updatedAt: '',
    breadcrumbs: [],
  };

  modelDescription = '';
  modelTags: string[] = [];
  mediaUrl = '';

  activityMetrics = {
    likes: 0,
    views: 0,
    downloads: 0,
  };

  private detailState = signal<Record<string, string>>({});

  readonly detailRows = computed(() => this.detailState());

  async ngOnInit(): Promise<void> {
    this.isLoggedIn.set(await this.keycloak.isLoggedIn());
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      this.modelId = id;
      if (id) {
        void this.loadModelDetails(id);
      } else {
        this.loading.set(false);
        this.toaster.error('Missing model id');
      }
    });
  }

  onLoginModalClose(): void {
    this.showLoginModal.set(false);
    void this.keycloak.isLoggedIn().then((v) => {
      this.isLoggedIn.set(v);
      if (v && this.modelId) {
        void this.refreshUserInteractionState(this.modelId);
      }
    });
  }

  async onHeaderBookmarkClick(): Promise<void> {
    const id = this.modelId;
    if (!id) return;
    try {
      const actionType = this.isBookmarked() ? 'UNBOOKMARK' : 'BOOKMARK';
      await this.userInteractionApi.createBookmarkOrLike({
        assetId: id,
        assetType: 'AI_MODEL',
        actionType,
      });
      this.isBookmarked.update((v) => !v);
      this.toaster.success(
        this.isBookmarked()
          ? "You've bookmarked the model successfully."
          : 'Bookmark removed.',
      );
    } catch (e) {
      console.error(e);
      this.toaster.error('Failed to update bookmark');
    }
  }

  async onHeaderLikeClick(): Promise<void> {
    const id = this.modelId;
    if (!id) return;
    try {
      const actionType = this.isLiked() ? 'DISLIKE' : 'LIKE';
      await this.userInteractionApi.createBookmarkOrLike({
        assetId: id,
        assetType: 'AI_MODEL',
        actionType,
      });
      const nextLiked = !this.isLiked();
      this.isLiked.set(nextLiked);
      this.activityMetrics.likes = Math.max(
        0,
        (this.activityMetrics.likes ?? 0) + (nextLiked ? 1 : -1),
      );
      this.toaster.success(
        nextLiked ? "You've liked the model successfully." : 'Like removed.',
      );
    } catch (e) {
      console.error(e);
      this.toaster.error('Failed to update like');
    }
  }

  private formatNumericDate(iso: string): string {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '—';
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    } catch {
      return '—';
    }
  }

  private formatRelativeUpdated(iso: string): string {
    if (!iso) return '—';
    try {
      const normalized = iso.trim();
      const t = new Date(
        /[Zz]$|[+-]\d{2}:?\d{2}$/.test(normalized)
          ? normalized
          : normalized.replace(/\.\d+$/, '') + 'Z',
      ).getTime();
      if (Number.isNaN(t))
        return `Updated ${this.formatService.formatDateForCard(iso)}`;
      const diff = Date.now() - t;
      const sec = Math.floor(diff / 1000);
      if (sec < 45) return 'Updated just now';
      const min = Math.floor(sec / 60);
      if (min < 60) return `Updated ${min} minute${min === 1 ? '' : 's'} ago`;
      const h = Math.floor(min / 60);
      if (h < 24) return `Updated ${h} hour${h === 1 ? '' : 's'} ago`;
      const d = Math.floor(h / 24);
      if (d < 14) return `Updated ${d} day${d === 1 ? '' : 's'} ago`;
      return `Updated ${this.formatService.formatDateForCard(iso)}`;
    } catch {
      return '—';
    }
  }

  private extractItem(response: unknown): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') return null;
    const r = response as Record<string, unknown>;
    let data: unknown = r;
    if (r['result'] != null) {
      const res = r['result'];
      data = Array.isArray(res) ? res[0] : res;
    } else if (r['data'] != null) {
      const res = r['data'];
      data = Array.isArray(res) ? res[0] : res;
    } else if (
      Array.isArray(r['items']) &&
      (r['items'] as unknown[]).length > 0
    ) {
      data = (r['items'] as unknown[])[0];
    } else if (Array.isArray(response) && (response as unknown[]).length > 0) {
      data = (response as unknown[])[0];
    }
    return data && typeof data === 'object'
      ? (data as Record<string, unknown>)
      : null;
  }

  private formatIndustryField(raw: unknown): string {
    if (raw == null) return '—';
    if (Array.isArray(raw)) {
      const parts = raw
        .filter((v) => v !== null && v !== undefined && v !== '')
        .map((v) => String(v));
      return parts.length ? parts.join(', ') : '—';
    }
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    return '—';
  }

  private formatFileSizeBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) return '—';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(
      Math.floor(Math.log(bytes) / Math.log(k)),
      sizes.length - 1,
    );
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  }

  /** API may return byte count (number), numeric string, or a human-readable string (e.g. "15MB"). */
  private formatFileSizeField(raw: unknown): string {
    if (raw == null || raw === '') return '—';
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return this.formatFileSizeBytes(raw);
    }
    const s = String(raw).trim();
    if (!s) return '—';
    if (/^\d+$/.test(s)) {
      return this.formatFileSizeBytes(Number(s));
    }
    return s;
  }

  private async loadModelDetails(id: string): Promise<void> {
    this.loading.set(true);
    this.isBookmarked.set(false);
    this.isLiked.set(false);
    try {
      const response = await this.catalogueApi.getItemById(id);
      const modelData = this.extractItem(response);

      if (!modelData) {
        this.toaster.error('Could not load model');
        this.loading.set(false);
        return;
      }

      const title =
        (modelData['label'] as string) ||
        (modelData['name'] as string) ||
        'Model';

      const publisher =
        (modelData['provider'] as string) ||
        (modelData['organisation'] as string) ||
        (modelData['organization'] as string) ||
        '';

      const headerShortDescription =
        String(modelData['shortDescription'] ?? '').trim() ||
        String(modelData['description'] ?? '').trim() ||
        String(modelData['summary'] ?? '').trim() ||
        '';

      const lastUpdatedRaw =
        (modelData['lastUpdated'] as string) ||
        (modelData['itemUpdatedAt'] as string) ||
        (modelData['itemCreatedAt'] as string) ||
        (modelData['createdAt'] as string) ||
        '';

      this.headerConfig = {
        title,
        shortDescription: headerShortDescription,
        updatedAt: this.formatRelativeUpdated(lastUpdatedRaw),
        breadcrumbs: [{ label: 'Models', route: '/models' }, { label: title }],
        assetId: id,
      };

      this.modelDescription =
        (modelData['description'] as string) ||
        (modelData['shortDescription'] as string) ||
        (modelData['summary'] as string) ||
        '';

      this.modelTags = [];
      const tags = modelData['tags'] ?? modelData['tag'];
      if (Array.isArray(tags)) {
        this.modelTags = tags.map((t) => String(t));
      }

      const permission = this.helper.extractAccessBadge(modelData) || 'Public';
      const orgName =
        (modelData['organisation'] as string) ||
        (modelData['organization'] as string) ||
        publisher ||
        '';

      // Match MahaAgX model detail: provider → organisation → organization only
      const uploadedByRaw =
        (modelData['provider'] as string) ||
        (modelData['organisation'] as string) ||
        (modelData['organization'] as string) ||
        '';
      const uploadedBy = String(uploadedByRaw).trim() || '—';

      const organisationType =
        String(
          modelData['organisationType'] ?? modelData['organizationType'] ?? '',
        ).trim() || '—';

      const industry = this.formatIndustryField(modelData['industry']);

      const modelType =
        String(modelData['modelType'] ?? modelData['type'] ?? '').trim() || '—';

      const fileFormat =
        String(
          modelData['fileFormat'] ??
            modelData['format'] ??
            modelData['file_format'] ??
            '',
        ).trim() || '—';

      const fileSize = this.formatFileSizeField(
        modelData['fileSize'] ?? modelData['file_size'] ?? modelData['size'],
      );

      const license =
        (modelData['license'] as string) ||
        (modelData['licence'] as string) ||
        '—';

      const lastUpdatedDisplay = lastUpdatedRaw
        ? this.formatService.formatDateForCard(lastUpdatedRaw)
        : '—';

      const createdAt = this.formatNumericDate(
        (modelData['itemCreatedAt'] as string) ||
          (modelData['createdAt'] as string) ||
          '',
      );

      const organisationName = orgName.trim() || '—';

      const mediaRaw =
        (modelData['mediaURL'] as string) ||
        (modelData['mediaUrl'] as string) ||
        (modelData['githubRepo'] as string) ||
        (modelData['repository'] as string) ||
        '';
      this.mediaUrl = typeof mediaRaw === 'string' ? mediaRaw.trim() : '';

      this.detailState.set({
        createdAt,
        fileFormat,
        fileSize,
        industry,
        lastUpdated: lastUpdatedDisplay,
        license,
        modelPermission: permission,
        modelType,
        organisationName,
        organisationType,
        publisher: publisher.trim() || '—',
        uploadedBy,
      });

      const metrics =
        (modelData['metrics'] as Record<string, number>) || modelData;
      this.activityMetrics = {
        likes:
          (metrics['likes'] as number) ??
          (metrics['likeCount'] as number) ??
          (metrics['like_count'] as number) ??
          0,
        views:
          (metrics['views'] as number) ??
          (metrics['viewCount'] as number) ??
          (metrics['view_count'] as number) ??
          0,
        downloads:
          (metrics['downloads'] as number) ??
          (metrics['downloadCount'] as number) ??
          (metrics['download_count'] as number) ??
          0,
      };

      if (this.isLoggedIn()) {
        void this.refreshUserInteractionState(id);
      }
    } catch (e) {
      console.error(e);
      this.toaster.error('Failed to load model');
    } finally {
      this.loading.set(false);
    }
  }

  private async refreshUserInteractionState(assetId: string): Promise<void> {
    try {
      const response = await this.userInteractionApi.userInteractions({
        assetId,
        assetType: 'AI_MODEL',
      });
      const { bookmarked, liked } =
        parseBookmarkLikeFromUserInteractions(response);
      this.isBookmarked.set(bookmarked);
      this.isLiked.set(liked);
    } catch {
      // Metrics already from catalogue item API
    }
  }
}
